import React, { useState } from 'react';
import loadImage from 'blueimp-load-image';
import PinturaPopup from './PinturaPopup';

interface ProfilePhotoProps {
  profile: {
    id: number;
  };
  authenticity_token: string;
  callback: (imageUrl: string) => void;
}

const ProfilePhoto: React.FC<ProfilePhotoProps> = (props) => {
  const [src, setSrc] = useState<string | null>(null);
  const [cropComplete, setCropComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Update filename display
      const fileLabel = e.target.nextElementSibling as HTMLLabelElement;
      if (fileLabel && fileLabel.querySelector('.file-name')) {
        const fileName = fileLabel.querySelector('.file-name') as HTMLSpanElement;
        fileName.textContent = file.name;
      }

      setFileError(null);
      loadImage(file, (img: any, data: any) => {
        // blueimp-load-image hands back an error Event, not a canvas, when the
        // browser cannot decode the file (HEIC on many Android browsers).
        // Sentry DIRECTDENTAL-20Z.
        if (!img || typeof img.toBlob !== 'function') {
          setFileError("We couldn't read that image. Please choose a JPG or PNG photo.");
          return;
        }
        img.toBlob((a: Blob) => {
          const reader = new FileReader();
          reader.addEventListener('load', () => setSrc(reader.result as string));
          reader.readAsDataURL(a);
        });
      }, { maxWidth: 2000, canvas: true, meta: true, orientation: true });
    }

    setCropComplete(false);
  };

  const isCropping = !cropComplete && src;

  return (
    <div>
      <div>
        {!isCropping && !loading && (
          <>
            <div className="d-flex justify-content-center">
              <div className="styled-file-input">
                <input
                  type="file"
                  onChange={onSelectFile}
                  id="avatar_field_original"
                  className="file-input-hidden"
                  accept="image/*"
                />
                <label htmlFor="avatar_field_original" className="file-input-label">
                  <i className="fas fa-cloud-upload-alt me-2"></i>
                  <span className="file-name">Choose photo...</span>
                </label>
              </div>
            </div>
            {fileError && (
              <p className="text-danger text-center mt-2 mb-0" role="alert"><small>{fileError}</small></p>
            )}
            <input type="hidden" id="avatar_field" name="profile[avatar]" value={src || ''} />
          </>
        )}
      </div>

      {!cropComplete && src && (
        <div className="mt-3">
          <p><small>Drag the corners and click Crop when you're finished.</small></p>
          <PinturaPopup
            avatar={src}
            setImageState={(img) => setSrc(img)}
            updateProfilePhoto={(base64data) => {
              setLoading(true);
              updateProfilePhoto(
                base64data,
                props.profile.id,
                props.authenticity_token,
                (e) => {
                  setLoading(false);
                  props.callback(e);
                }
              );
            }}
          />
        </div>
      )}

      <div className="text-center">
        {loading && (
          <div className="py-3">
            <h5 className="text-center pt-2">Saving Photo...</h5>
          </div>
        )}
      </div>
    </div>
  );
};

const updateProfilePhoto = (photo: string, id: number, authenticity_token: string, callback: (imageUrl: string) => void) => {
  const url = `/profiles/${id}.json`;
  const method = "put";
  const data = { profile: { avatar: photo } };

  fetch(url, {
    method: method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": authenticity_token,
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      if (response.ok) {
        return response.json()
      }
    })
    .then((response) => {
      callback(response.image)
    })
    .catch((error) => console.error("Error:", error))
}

export default ProfilePhoto;
